import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Banner } from '@eprintsmart/shared';
import { createBanner, deleteBanner, updateBanner, watchBanners } from '../services/banners';

/** Manage the marketing banners in the storefront home hero. Upload a wide
 * image (the whole banner is the artwork) and, optionally, a link it points to.
 * The lowest-order active banner shows first; several make a rotating hero. */
export function Banners() {
  const [rows, setRows] = useState<Banner[]>([]);
  useEffect(() => watchBanners(setRows), []);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Banners</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Marketing banners shown in the home-page hero. Upload a wide image (landscape, roughly 3:2 —
        e.g. 1200×800 — looks best). Add a link so clicking it opens a page. Toggle “Live” to show or
        hide, and set the order for rotation.
      </Typography>

      <NewBanner nextOrder={(rows[rows.length - 1]?.order ?? 0) + 1} />

      <Stack spacing={1.5} sx={{ mt: 3 }}>
        {rows.length === 0 ? (
          <Typography color="text.secondary">No banners yet.</Typography>
        ) : (
          rows.map((b) => (
            <Paper key={b.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={b.imageUrl}
                alt={b.alt ?? ''}
                sx={{ width: 144, height: 96, objectFit: 'cover', borderRadius: 1, bgcolor: '#f1f1f1', flexShrink: 0 }}
              />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography fontWeight={600} noWrap>{b.alt || 'Banner'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {b.linkUrl ? `→ ${b.linkUrl}` : 'no link'} · order {b.order}
                </Typography>
              </Box>
              <Chip size="small" label={b.active ? 'Live' : 'Hidden'} color={b.active ? 'success' : 'default'} />
              <Switch checked={b.active} onChange={(e) => void updateBanner(b.id, { active: e.target.checked })} />
              <IconButton onClick={() => { if (confirm('Delete this banner?')) void deleteBanner(b.id); }}>
                <DeleteOutlineIcon />
              </IconButton>
            </Paper>
          ))
        )}
      </Stack>
    </Box>
  );
}

function NewBanner({ nextOrder }: { nextOrder: number }) {
  const [alt, setAlt] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [order, setOrder] = useState(nextOrder);
  const [image, setImage] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imageRef = useRef<HTMLInputElement>(null);

  useEffect(() => setOrder(nextOrder), [nextOrder]);

  const save = async () => {
    if (!image) return;
    setSaving(true);
    setError(null);
    try {
      await createBanner({
        imageFile: image,
        alt: alt.trim() || undefined,
        linkUrl: linkUrl.trim() || undefined,
        order,
      });
      setAlt(''); setLinkUrl(''); setImage(null);
      if (imageRef.current) imageRef.current.value = '';
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Upload failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Add a banner</Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Alt text / caption" placeholder="e.g. Design your visiting cards with AI"
            value={alt} onChange={(e) => setAlt(e.target.value)} fullWidth
          />
          <TextField
            label="Link (optional)" placeholder="e.g. /design or /visiting-cards"
            value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} fullWidth
          />
          <TextField
            label="Order" type="number" value={order} onChange={(e) => setOrder(Number(e.target.value) || 0)}
            sx={{ width: 110 }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Button variant="outlined" component="label">
            {image ? `Image: ${image.name}` : 'Choose banner image *'}
            <input ref={imageRef} type="file" accept="image/*" hidden onChange={(e) => setImage(e.target.files?.[0] ?? null)} />
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={() => void save()} disabled={saving || !image}>
            {saving ? 'Uploading…' : 'Add banner'}
          </Button>
        </Stack>
        {error && <Typography color="error" variant="body2">{error}</Typography>}
      </Stack>
    </Paper>
  );
}
